import type { ReactNode } from 'react'

type ResultsHeadingProps = {
  count: number
  specialtyLabel: string
  specialtyPluralLabel: string
  /**
   * The location as rendered inline in the heading. A slot rather than a string
   * so the results page can pass the location picker's trigger (VIT-17740) while
   * this component stays presentational — and router-free, so its unit test can
   * stay a plain static render.
   */
  location: ReactNode
  /** The plan `?insurance=` resolved to, or null when the search is unfiltered. */
  insuranceLabel?: string | null | undefined
}

/**
 * "12 Cardiologists in Portland, OR" — the results page h1 (VIT-17719).
 *
 * `count` is the number of provider cards actually rendered, not the raw
 * recommendation count, so the heading can't overstate the results.
 */
export const ResultsHeading = ({
  count,
  specialtyLabel,
  specialtyPluralLabel,
  location,
  insuranceLabel = null,
}: ResultsHeadingProps) => (
  <h1
    data-testid="results-heading"
    className="font-medium text-secondary-700 text-3xl tracking-tight sm:text-4xl font-display mb-6 text-balance"
  >
    {count} {count === 1 ? specialtyLabel : specialtyPluralLabel} in {location}
    {/* Appended rather than woven in before the location, so the location stays
        one contiguous slot for the picker trigger. The verb agrees with `count`
        for the same reason the specialty label does — a widened search returning
        exactly one provider is a common case, not an edge case. `text-balance`
        on the h1 lets a long plan name wrap to a third line rather than overflow. */}
    {insuranceLabel != null && ` ${count === 1 ? 'who accepts' : 'who accept'} ${insuranceLabel}`}
  </h1>
)
